import { useState, useMemo, useCallback } from "react";
import { Form, Select, Input, Tooltip, Avatar } from "antd";
import { InfoCircleOutlined, UserOutlined } from "@ant-design/icons";

import { AppStrings } from "../../../utils/constants/AppStrings";
import { formatString } from "../../../utils/constants/stringUtils";
import KpiCard from "../../CommonComponents/Dashboard/KpiCard/KpiCard";
import { BRAND_ICONS_URLS, KPI_CARD_BACKGROUND } from "../../../utils/Constants";
import { getColorBasedOnValue } from "../../../utils/colorUtils";
import styles from "./stylesPromosManualActions.module.css";
import DataTable from "../../shared/dashboards/DataTable/DataTable";
import AgentPerformanceView from "../AgentPerformanceView/AgentPerformanceView";
import { NAVIGATION_ROUTES } from "../../../utils/NavigationUtils";
import { getManualActionsStaffManagementHref } from "../manualActionsStaffDeepLink";
import { convertToUserTimezone } from "../../../utils/dateService";
import CustomAutoCompleteControlled from "../../CommonComponents/CustomAutoComplete/CustomAutoCompleteControlled";
import { ChevronDown, ChevronUp } from "react-feather";
import { useGetAALPromosTable } from "../../../queries/agent-audit-log/promos/useGetAALPromosTable";
import type { PromosAALTableFilters } from "../../../queries/agent-audit-log/promos/useGetAALPromosTable";
import { useGetAALPromosKPIs } from "../../../queries/agent-audit-log/promos/useGetAALPromosKPIs";
import { useDownloadAALPromosTable } from "../../../queries/agent-audit-log/promos/useDownloadAALPromosTable";
import {
  useDownloadOnTrigger,
} from "../../../hooks/useDownloadOnTrigger";
import { useGetRoles } from "../../../queries/useRolesApi";
import { useGetStaff } from "../../../queries/useGetStaff";
import { useManualActionsFlags } from "../../../hooks/useFeatureFlag";
import { filterOptionsFor } from "../../../queries/agent-audit-log/aalV2";
import {
  applyAntdSorterToSortState,
  manualActionsQueryLoading,
  toAalSort,
  useResetManualActionsPageOnApvRoleChange,
  useResetManualActionsTableOnApvToggle,
} from "../manualActionsTableShared";

type DayjsLike = {
  utc?: () => { format: (pattern: string) => string };
};

type PromosManualActionsProps = {
  agentPerformanceView?: boolean;
  showFilters?: boolean;
  dates?: [DayjsLike, DayjsLike];
  downloadTrigger?: number;
};

type KpiData = {
  free_picks_amount?: number | string;
  free_picks_amount_pct_change?: number | null;
  free_picks_amount_realized?: number | string;
  free_picks_amount_realized_pct_change?: number | null;
  free_picks_given?: number;
  free_picks_given_pct_change?: number | null;

  profit_boosts_amount_realized?: number | string;
  profit_boosts_amount_realized_pct_change?: number | null;
  profit_boosts_given?: number;
  profit_boosts_given_pct_change?: number | null;

  free_spins_amount_realized?: number | string;
  free_spins_amount_realized_pct_change?: number | null;
  free_spins_given?: number;
  free_spins_given_pct_change?: number | null;
};

type StaffOption = { id: string; email: string };
type Role = { id: string; name: string; color?: string };

const TypedAgentAutoComplete = CustomAutoCompleteControlled as any;
const TypedDataTable = DataTable as any;

const INITIAL_PANEL_FILTERS = {
  agent: "",
  role: null as string | null,
  username: "",
  promo_type: null as string | null,
  promo_status: null as string | null,
};

const promoTypeOptions: { value: string; label: string }[] = [
  { value: "free_pick", label: AppStrings.FREE_PICK },
  { value: "profit_boost", label: AppStrings.PROFIT_BOOST },
  { value: "free_spin", label: AppStrings.FREE_SPIN },
  { value: "claim_bonus", label: AppStrings.PAM_CLAIM_BONUS },
];

const statusOptions: { value: string; label: string }[] = [
  { value: "claimed", label: AppStrings.CLAIMED },
  { value: "unclaimed", label: AppStrings.UNCLAIMED },
  { value: "expired", label: AppStrings.EXPIRED },
  { value: "removed", label: AppStrings.REMOVED },
];

/** Matches API `promo_status` values (lowercase). Server `status_color` overrides when present. */
const PROMO_STATUS_COLOR: Record<string, string> = {
  claimed: "#71BF8A",
  unclaimed: "#F97316",
  expired: "#E74C3C",
  removed: "#64748B",
};

function normalizePromoStatus(raw: unknown): string | null {
  if (raw == null || raw === "") return null;
  return String(raw).trim().toLowerCase();
}

function labelForPromoStatus(key: string | null): string {
  if (!key) return "";
  return key.charAt(0).toUpperCase() + key.slice(1);
}

function promosSortFieldForApi(field: string | null): string | null {
  if (field === "created_at" || field === "promo_time_granted") return "date";
  return field;
}

/** Map APV API rows to KPI field names (supports legacy aliases from older responses). */
function normalizeApvTableRow(row: Record<string, unknown>, index: number) {
  const r = row as Record<string, any>;
  return {
    ...r,
    key: r.agent_id ?? r.event_id ?? String(index),
    free_picks_amount: r.free_picks_amount ?? r.sb_free_pick_value,
    free_picks_amount_pct_change:
      r.free_picks_amount_pct_change ?? r.sb_free_pick_pct_change,
    free_picks_amount_realized: r.free_picks_amount_realized,
    free_picks_amount_realized_pct_change: r.free_picks_amount_realized_pct_change,
    free_picks_given: r.free_picks_given ?? r.sb_free_picks,
    free_picks_given_pct_change:
      r.free_picks_given_pct_change ?? r.sb_free_picks_pct_change,
    profit_boosts_amount_realized: r.profit_boosts_amount_realized,
    profit_boosts_amount_realized_pct_change:
      r.profit_boosts_amount_realized_pct_change,
    profit_boosts_given: r.profit_boosts_given ?? r.profit_boosts,
    profit_boosts_given_pct_change:
      r.profit_boosts_given_pct_change ?? r.profit_boosts_pct_change,
    free_spins_amount_realized: r.free_spins_amount_realized,
    free_spins_amount_realized_pct_change: r.free_spins_amount_realized_pct_change,
    free_spins_given: r.free_spins_given ?? r.free_spins,
    free_spins_given_pct_change:
      r.free_spins_given_pct_change ?? r.free_spins_pct_change,
  };
}

function PromosManualActions({
  agentPerformanceView = false,
  showFilters = false,
  dates,
  downloadTrigger = 0,
}: PromosManualActionsProps) {
  const [form] = Form.useForm();
  const watchedAgent = (Form.useWatch("agent", form) ?? "") as string;
  const watchedRole = Form.useWatch("role", form) as string | null | undefined;
  const watchedUsername = (Form.useWatch("username", form) ?? "") as string;
  const watchedPromoType = Form.useWatch("promo_type", form) as
    | string
    | null
    | undefined;
  const watchedPromoStatus = Form.useWatch("promo_status", form) as
    | string
    | null
    | undefined;

  const [autoCompleteKey, setAutoCompleteKey] = useState<number>(0);
  /** CustomAutoComplete is not a Form.Item; useWatch("agent") does not update — bump this on agent change so `hasFiltersFilled` recomputes (see WalletManualActions). */
  const [agentFieldSeq, setAgentFieldSeq] = useState(0);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_PANEL_FILTERS);
  const [selectedRole, setSelectedRole] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(100);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"ascend" | "descend" | null>(null);
  const [showAdditionalKpis, setShowAdditionalKpis] = useState<boolean>(false);

  const { showCasinoKpi, showSportsbookKpi } = useManualActionsFlags();

  const hasFiltersFilled = useMemo(() => {
    void agentFieldSeq;
    const agentFromForm = String(form.getFieldValue("agent") ?? "").trim();
    const agentFilled =
      agentFromForm !== "" ||
      Boolean(watchedAgent && String(watchedAgent).trim() !== "");
    const roleFilled = watchedRole != null && watchedRole !== "";
    const usernameFilled = (watchedUsername ?? "").trim() !== "";
    const promoTypeFilled = watchedPromoType != null && watchedPromoType !== "";
    const promoStatusFilled =
      watchedPromoStatus != null && watchedPromoStatus !== "";
    return agentFilled || roleFilled || usernameFilled || promoTypeFilled || promoStatusFilled;
  }, [
    agentFieldSeq,
    watchedAgent,
    watchedRole,
    watchedUsername,
    watchedPromoType,
    watchedPromoStatus,
    form,
  ]);

  const start_date = dates?.[0]?.utc?.().format("YYYY-MM-DD");
  const end_date = dates?.[1]?.utc?.().format("YYYY-MM-DD");
  const listView = agentPerformanceView ? "agents" : "events";

  const tableFilters: PromosAALTableFilters = {
    view: listView,
    start_date: start_date ?? "",
    end_date: end_date ?? "",
    page: currentPage,
    size: pageSize,
    sort: toAalSort(promosSortFieldForApi(sortField), sortOrder),
    ...(agentPerformanceView
      ? selectedRole.length > 0 && { agent_role_id: selectedRole[0] }
      : appliedFilters.role && { agent_role_id: appliedFilters.role }),
    ...(!agentPerformanceView && appliedFilters.agent && { agent_id: appliedFilters.agent }),
    ...(!agentPerformanceView && appliedFilters.username && { username: appliedFilters.username }),
    ...(!agentPerformanceView &&
      appliedFilters.promo_type && { promo_type: appliedFilters.promo_type }),
    ...(!agentPerformanceView &&
      appliedFilters.promo_status && { promo_status: appliedFilters.promo_status }),
  };

  const kpiFilters = {
    start_date: start_date ?? "",
    end_date: end_date ?? "",
    ...(appliedFilters.promo_status && { promo_status: appliedFilters.promo_status }),
  };

  const {
    items: tableItems,
    total: totalResults,
    filters: tableFiltersMeta,
    isLoading: isTableLoading,
    isFetching: isTableFetching,
  } = useGetAALPromosTable(tableFilters);

  const serverPromoTypeOptions = filterOptionsFor(
    tableFiltersMeta ?? [],
    "promo_type",
  ).map((option) => ({
    ...option,
    label:
      option.value === "claim_bonus"
        ? AppStrings.PAM_CLAIM_BONUS
        : promoTypeOptions.find((item) => item.value === option.value)?.label ??
          option.label,
  }));
  const serverPromoStatusOptions = filterOptionsFor(
    tableFiltersMeta ?? [],
    "promo_status",
  ).map((option) => ({
    ...option,
    label:
      statusOptions.find((item) => item.value === option.value)?.label ??
      option.label,
  }));
  const effectivePromoTypeOptions = serverPromoTypeOptions.length
    ? serverPromoTypeOptions
    : promoTypeOptions;
  const effectivePromoStatusOptions = serverPromoStatusOptions.length
    ? serverPromoStatusOptions
    : statusOptions;

  const { data: kpiData, isLoading: isKpiLoading } = useGetAALPromosKPIs(kpiFilters);

  const { data: roles } = useGetRoles() as { data?: Role[] };
  const { data: staffData } = useGetStaff({
    page_size: 100,
  }) as { data?: { items?: StaffOption[] } };

  const agentOptions = (staffData?.items ?? []).map((s) => ({
    value: s.email,
    label: s.email,
    id: s.id,
  }));

  const roleOptions = (roles ?? []).map((r) => ({
    value: r.id,
    label: r.name,
  }));

  const roleById = Object.fromEntries(
    (roles ?? []).map((r) => [r.id, { name: r.name, color: r.color ?? "#6b7280" }]),
  ) as Record<string, { name: string; color: string }>;

  useResetManualActionsPageOnApvRoleChange(
    agentPerformanceView,
    selectedRole,
    setCurrentPage,
  );

  useResetManualActionsTableOnApvToggle(
    agentPerformanceView,
    setSelectedRole,
    setSortField,
    setSortOrder,
    setCurrentPage,
  );

  const { start: downloadTable } = useDownloadAALPromosTable(listView);
  useDownloadOnTrigger(downloadTrigger, downloadTable, () => ({
    start_date: start_date ?? "",
    end_date: end_date ?? "",
    ...(agentPerformanceView
      ? selectedRole.length > 0
        ? { agent_role_id: selectedRole[0] }
        : {}
      : {
          ...(appliedFilters.agent && { agent_id: appliedFilters.agent }),
          ...(appliedFilters.role && { agent_role_id: appliedFilters.role }),
          ...(appliedFilters.username && { username: appliedFilters.username }),
          ...(appliedFilters.promo_type && { promo_type: appliedFilters.promo_type }),
          ...(appliedFilters.promo_status && { promo_status: appliedFilters.promo_status }),
        }),
  }));

  const apvDataSource = useMemo(
    () => tableItems.map((row: any, index: number) => normalizeApvTableRow(row, index)),
    [tableItems],
  );

  const currencyImageSrc =
    (process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL as string) +
    BRAND_ICONS_URLS.primaryCurrencySymbol as unknown as string;

  const formatCurrency = (value: number | string, percentage = false) =>
    new Intl.NumberFormat("en-US", {
      style: percentage ? "percent" : "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(value ?? 0));

  const handleClearFilters = () => {
    form.resetFields();
    form.setFieldsValue({
      agent: "",
      username: "",
      role: null,
      promo_type: null,
      promo_status: null,
    });
    setAutoCompleteKey((k) => k + 1);
    setAgentFieldSeq((n) => n + 1);
    setAppliedFilters(INITIAL_PANEL_FILTERS);
    setCurrentPage(1);
  };

  const handleApply = () => {
    const values = form.getFieldsValue();
    setAppliedFilters({
      agent: form.getFieldValue("agent") ?? "",
      role: values.role ?? null,
      username: values.username ?? "",
      promo_type: values.promo_type ?? null,
      promo_status: values.promo_status ?? null,
    });
    setAgentFieldSeq((n) => n + 1);
    setCurrentPage(1);
  };

  const handleTableChange = useCallback(
    (_pagination: unknown, _filters: unknown, sorter: unknown) => {
      applyAntdSorterToSortState(sorter, setSortField, setSortOrder);
    },
    [],
  );

  const handlePaginationChange = useCallback((page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  }, []);

  const renderPercent = (percent: number | null | undefined) => {
    if (percent == null || Number.isNaN(Number(percent))) return null;
    const n = Number(percent);
    const sign = n >= 0 ? "+" : "";
    return (
      <span
        style={{
          marginLeft: "4px",
          color: getColorBasedOnValue(n),
          fontSize: "11px",
        }}
      >
        {sign}
        {n.toFixed(1)}%
      </span>
    );
  };

  const k = (kpiData as KpiData) ?? {};

  const columns: any[] = [
    {
      title: AppStrings.DATE_AND_TIME,
      dataIndex: "promo_time_granted",
      key: "promo_time_granted",
      sorter: true,
      showSorterTooltip: false,
      width: 250,
      render: (text: string) => <span>{convertToUserTimezone(text)}</span>,
    },
    {
      title: AppStrings.USERNAME,
      dataIndex: "target_username",
      key: "target_username",
      width: 200,
      render: (text: string, record: any) => (
        <div className={styles["promos-manual-actions-username-cell"]}>
          <Avatar size={20} icon={<UserOutlined />} style={{ backgroundColor: "#6B7280" }} className={styles["promos-manual-actions-avatar"]} />
          <a
            href={`${NAVIGATION_ROUTES.USER_MANAGER}?userId=${record.target_user_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles["promos-manual-actions-hoverable"]}
          >
            {text}
          </a>
        </div>
      ),
    },
    {
      title: AppStrings.PROMO_TYPE,
      dataIndex: "promo_type",
      key: "promo_type",
      width: 200,
    },
    {
      title: AppStrings.AMOUNT,
      dataIndex: "amount",
      key: "amount",
      width: 140,
      align: "right",
      sorter: true,
      showSorterTooltip: false,
      render: (value: number | string, record: any) => {
        const num = Number(value);
        if (record.promo_type === "profit_boost") {
          return <span style={{ color: "#6B7280" }}>-</span>;
        }
        return (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              justifyContent: "flex-end",
            }}
          >
            <img src={currencyImageSrc} alt="" style={{ width: "14px", height: "14px" }} />
            {formatCurrency(num, false)}
          </span>
        );
      },
    },
    {
      title: AppStrings.PROMO_PERCENTAGE,
      dataIndex: "promo_percentage",
      key: "promo_percentage",
      hidden:
        watchedPromoType != null &&
        watchedPromoType !== "" &&
        watchedPromoType !== "profit_boost",
      width: 250,
      align: "right",
      render: (value: number | string, record: any) => {
        if (record.promo_type !== "profit_boost") {
          return <span style={{ color: "#6B7280" }}>—</span>;
        }
        const num = Number(value) / 100;
        return (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              justifyContent: "flex-end",
            }}
          >
            {formatCurrency(num, true)}
          </span>
        );
      },
    },
    {
      title: (
        <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
          {AppStrings.REALIZED_VALUE}
          <Tooltip title={AppStrings.USER_REALIZED_VALUE_TOOLTIP}>
            <InfoCircleOutlined style={{ color: "var(--grey-400)", fontSize: "12px", cursor: "help" }} />
          </Tooltip>
        </span>
      ),
      dataIndex: "amount_realized",
      key: "amount_realized",
      width: 250,
      align: "right",
      render: (value: number | string) => {
        return (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              justifyContent: "flex-end",
            }}
          >
            <img src={currencyImageSrc} alt="" style={{ width: "14px", height: "14px" }} />
            {formatCurrency(value)}
          </span>
        );
      },
    },
    {
      title: AppStrings.PAM_AGENT,
      dataIndex: "agent_email",
      key: "agent_email",
      width: 200,
      render: (text: string, record: any) => (
        <a
          href={getManualActionsStaffManagementHref(record.agent_id)}
          target="_blank"
          rel="noopener noreferrer"
          className={styles["promos-manual-actions-hoverable"]}
        >
          {text}
        </a>
      ),
    },
    {
      title: AppStrings.REASON,
      dataIndex: "reason",
      key: "reason",
      width: 200,
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: AppStrings.STATUS,
      dataIndex: "promo_status",
      key: "promo_status",
      width: 150,
      align: "center",
      render: (text: string, record: any) => {
        const key = normalizePromoStatus(text);
        if (!key) {
          return <span style={{ color: "#6B7280" }}>—</span>;
        }
        const bg = (record as any).status_color || PROMO_STATUS_COLOR[key] || "#6b7280";
        return (
          <span className={styles["promos-manual-actions-status-badge"]} style={{ backgroundColor: bg }}>
            {labelForPromoStatus(key)}
          </span>
        );
      },
    },
    {
      title: AppStrings.TIME_CLAIMED,
      dataIndex: "promo_time_claimed",
      key: "promo_time_claimed",
      width: 200,
      render: (_text: string, record: any) => {
        return record.promo_time_claimed ? (
          convertToUserTimezone(record.promo_time_claimed)
        ) : (
          <span style={{ color: "#6B7280" }}>—</span>
        );
      },
    },
  ];

  const agentPerformanceColumns: any[] = [
    {
      title: AppStrings.PAM_AGENT,
      dataIndex: "agent_email",
      key: "agent_email",
      width: 220,
      hidden: !showSportsbookKpi,
      render: (text: string, record: any) => (
        <a
          href={getManualActionsStaffManagementHref(record.agent_id)}
          target="_blank"
          rel="noopener noreferrer"
          className={styles["promos-manual-actions-hoverable"]}
        >
          {text}
        </a>
      ),
    },
    {
      title: (
        <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
          {`${AppStrings.DELTA_SYMBOL} ${AppStrings.FREE_PICKS_AMOUNT}`}
          <Tooltip title={AppStrings.FREE_PICKS_AMOUNT_TOOLTIP}>
            <InfoCircleOutlined style={{ color: "var(--grey-400)", fontSize: "12px", cursor: "help" }} />
          </Tooltip>
        </span>
      ),
      dataIndex: "free_picks_amount",
      key: "free_picks_amount",
      width: 200,
      sorter: true,
      hidden: !showSportsbookKpi,
      render: (text: number, record: any) => (
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <img src={currencyImageSrc} alt="" style={{ width: "14px", height: "14px" }} />
          {formatCurrency(text ?? 0)}
          {renderPercent(record.free_picks_amount_pct_change)}
        </span>
      ),
    },
    {
      title: (
        <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
          {AppStrings.FREE_PICKS_REALIZED_VALUE}
          <Tooltip title={formatString(AppStrings.FREE_PICKS_REALIZED_VALUE_TOOLTIP, { currency: AppStrings.PRIMARY_CURRENCY })}>
            <InfoCircleOutlined style={{ color: "var(--grey-400)", fontSize: "12px", cursor: "help" }} />
          </Tooltip>
        </span>
      ),
      dataIndex: "free_picks_amount_realized",
      key: "free_picks_amount_realized",
      width: 200,
      sorter: true,
      hidden: !showSportsbookKpi,
      render: (text: number, record: any) => (
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <img src={currencyImageSrc} alt="" style={{ width: "14px", height: "14px" }} />
          {formatCurrency(text ?? 0)}
          {renderPercent(record.free_picks_amount_realized_pct_change)}
        </span>
      ),
    },
    {
      title: `${AppStrings.DELTA_SYMBOL} ${AppStrings.NUMBER_OF_FREE_PICKS}`,
      dataIndex: "free_picks_given",
      key: "free_picks_given",
      width: 160,
      sorter: true,
      hidden: !showSportsbookKpi,
      render: (text: number, record: any) => (
        <span>
          {text ?? 0}
          {renderPercent(record.free_picks_given_pct_change)}
        </span>
      ),
    },
    {
      title: (
        <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
          {AppStrings.PROFIT_BOOST_REALIZED_VALUE}
          <Tooltip title={formatString(AppStrings.PROFIT_BOOST_REALIZED_VALUE_TOOLTIP, { currency: AppStrings.PRIMARY_CURRENCY })}>
            <InfoCircleOutlined style={{ color: "var(--grey-400)", fontSize: "12px", cursor: "help" }} />
          </Tooltip>
        </span>
      ),
      dataIndex: "profit_boosts_amount_realized",
      key: "profit_boosts_amount_realized",
      width: 200,
      sorter: true,
      hidden: !showSportsbookKpi,
      render: (text: number, record: any) => (
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <img src={currencyImageSrc} alt="" style={{ width: "14px", height: "14px" }} />
          {formatCurrency(text ?? 0)}
          {renderPercent(record.profit_boosts_amount_realized_pct_change)}
        </span>
      ),
    },
    {
      title: `${AppStrings.DELTA_SYMBOL} ${AppStrings.PROFIT_BOOSTS}`,
      dataIndex: "profit_boosts_given",
      key: "profit_boosts_given",
      width: 170,
      sorter: true,
      hidden: !showSportsbookKpi,
      render: (text: number, record: any) => (
        <span>
          {text ?? 0}
          {renderPercent(record.profit_boosts_given_pct_change)}
        </span>
      ),
    },
    {
      title: (
        <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
          {AppStrings.FREE_SPINS_REALIZED_VALUE}
          <Tooltip title={formatString(AppStrings.FREE_SPINS_REALIZED_VALUE_TOOLTIP, { currency: AppStrings.PRIMARY_CURRENCY })}>
            <InfoCircleOutlined style={{ color: "var(--grey-400)", fontSize: "12px", cursor: "help" }} />
          </Tooltip>
        </span>
      ),
      dataIndex: "free_spins_amount_realized",
      key: "free_spins_amount_realized",
      width: 200,
      sorter: true,
      hidden: !showCasinoKpi,
      render: (text: number, record: any) => (
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <img src={currencyImageSrc} alt="" style={{ width: "14px", height: "14px" }} />
          {formatCurrency(text ?? 0)}
          {renderPercent(record.free_spins_amount_realized_pct_change)}
        </span>
      ),
    },
    {
      title: `${AppStrings.DELTA_SYMBOL} ${AppStrings.FREE_SPINS}`,
      dataIndex: "free_spins_given",
      key: "free_spins_given",
      width: 160,
      sorter: true,
      hidden: !showCasinoKpi,
      render: (text: number, record: any) => (
        <span>
          {text ?? 0}
          {renderPercent(record.free_spins_given_pct_change)}
        </span>
      ),
    },
    {
      title: AppStrings.ROLE,
      dataIndex: "agent_role_id",
      key: "agent_role_id",
      width: 170,
      render: (value: string) => {
        const role = roleById[value];
        if (!role) return <span>{value || "—"}</span>;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: role.color,
                flexShrink: 0,
                display: "inline-block",
              }}
            />
            <span>{role.name}</span>
          </div>
        );
      },
    },
  ];

  const tableLoading = manualActionsQueryLoading(isTableLoading, isTableFetching);

  return (
    <div className={styles["promos-manual-actions-container"]}>
      <Form form={form}>
        <div
          className={styles["promos-manual-actions-selectors"]}
          style={{ display: showFilters && !agentPerformanceView ? "flex" : "none" }}
        >
          <div className={styles["promos-manual-actions-selector"]}>
            <span>{AppStrings.PAM_AGENT}</span>
            <TypedAgentAutoComplete
              key={autoCompleteKey}
              allOptions={agentOptions}
              selector="id"
              value={watchedAgent}
              handleChange={(id: string) => {
                form.setFieldValue("agent", id);
                setAgentFieldSeq((n) => n + 1);
              }}
              clearOptions={() => {
                form.setFieldValue("agent", "");
                setAgentFieldSeq((n) => n + 1);
              }}
              placeholder={AppStrings.NOT_SELECTED}
            />
          </div>
          <div className={styles["promos-manual-actions-selector"]}>
            <span>{AppStrings.PROMO_TYPE}</span>
            <Form.Item name="promo_type" noStyle>
              <Select
                allowClear
                placeholder={AppStrings.NOT_SELECTED}
                options={effectivePromoTypeOptions}
              />
            </Form.Item>
          </div>
          <div className={styles["promos-manual-actions-selector"]}>
            <span>{AppStrings.STATUS}</span>
            <Form.Item name="promo_status" noStyle>
              <Select
                allowClear
                placeholder={AppStrings.NOT_SELECTED}
                options={effectivePromoStatusOptions}
              />
            </Form.Item>
          </div>
          <div className={styles["promos-manual-actions-selector"]}>
            <span>{AppStrings.USERNAME}</span>
            <Form.Item name="username" noStyle>
              <Input
                placeholder={AppStrings.USERNAME}
                className={styles["promos-manual-actions-filter-input"]}
              />
            </Form.Item>
          </div>
          <div className={styles["promos-manual-actions-selector"]}>
            <span>{AppStrings.ROLE}</span>
            <Form.Item name="role" noStyle>
              <Select allowClear placeholder={AppStrings.NOT_SELECTED} options={roleOptions} />
            </Form.Item>
          </div>
        </div>
      </Form>
      <div
        className={styles["promos-manual-actions-selector-action-buttons"]}
        style={{ display: showFilters && !agentPerformanceView ? "flex" : "none" }}
      >
        <button
          type="button"
          className={`${styles.clear} ${hasFiltersFilled ? styles.dirty : ""}`}
          onClick={handleClearFilters}
        >
          {AppStrings.CLEAR_FILTERS}
        </button>
        <button
          type="button"
          className={`${styles.apply} ${hasFiltersFilled ? styles.dirty : ""}`}
          onClick={handleApply}
        >
          {AppStrings.APPLY}
        </button>
      </div>
      <div className={styles["promos-manual-actions-kpi-cards"]} style={{ display: !agentPerformanceView ? "flex" : "none" }}>
        <div className={styles["promos-kpi-primary-row"]}>
          <div className={`${styles["row"]} ${styles["promos-kpi-primary-cards"]}`}
          style={{ display: showSportsbookKpi ? "flex" : "none" }}>
            <div style={{ position: "relative", flex: 1, maxWidth: "33.33%" }}>
              <Tooltip title={AppStrings.FREE_PICKS_AMOUNT_TOOLTIP}>
                <InfoCircleOutlined
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    color: "var(--grey-400)",
                    fontSize: 14,
                    cursor: "help",
                    zIndex: 1,
                  }}
                />
              </Tooltip>
              <KpiCard
                title={`${AppStrings.DELTA_SYMBOL} ${AppStrings.FREE_PICKS_AMOUNT}`}
                value={Number(k.free_picks_amount ?? 0)}
                percentage={k.free_picks_amount_pct_change as number}
                hugeValue={false}
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
                isApiRunning={isKpiLoading}
                maximumFractionDigits={2}
              />
            </div>
            <div style={{ position: "relative", flex: 1, maxWidth: "33.33%" }}>
              <Tooltip title={formatString(AppStrings.FREE_PICKS_REALIZED_VALUE_TOOLTIP, { currency: AppStrings.PRIMARY_CURRENCY })}>
                <InfoCircleOutlined
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    color: "var(--grey-400)",
                    fontSize: 14,
                    cursor: "help",
                    zIndex: 1,
                  }}
                />
              </Tooltip>
              <KpiCard
                title={`${AppStrings.FREE_PICKS_REALIZED_VALUE}`}
                value={Number(k.free_picks_amount_realized ?? 0)}
                percentage={k.free_picks_amount_realized_pct_change as number}
                hugeValue={false}
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
                isApiRunning={isKpiLoading}
                maximumFractionDigits={2}
              />
            </div>
            <div style={{ position: "relative", flex: 1, maxWidth: "33.33%" }}>
              <KpiCard
                title={`${AppStrings.DELTA_SYMBOL} ${AppStrings.NUMBER_OF_FREE_PICKS}`}
                value={Number(k.free_picks_given ?? 0)}
                percentage={k.free_picks_given_pct_change as number}
                hugeValue={false}
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
                isApiRunning={isKpiLoading}
                maximumFractionDigits={0}
                displayAsRawText={true}
              />
            </div>
          </div>
          {(showSportsbookKpi || showCasinoKpi) && (
            <Tooltip title={AppStrings.ADDITIONAL_KPIS} placement="left">
              <button
                type="button"
                className={`${styles["promos-additional-kpis-chevron"]} ${showAdditionalKpis ? styles["is-open"] : ""}`}
                aria-expanded={showAdditionalKpis}
                aria-label={AppStrings.ADDITIONAL_KPIS}
                onClick={() => setShowAdditionalKpis(!showAdditionalKpis)}
              >
                {showAdditionalKpis ? <ChevronUp size={18} strokeWidth={2} /> : <ChevronDown size={18} strokeWidth={2} />}
              </button>
            </Tooltip>
          )}
        </div>

        <div className={styles["row"]} style={{ display: showAdditionalKpis && showSportsbookKpi ? "flex" : "none" }}>
          <div style={{ position: "relative", flex: 1, maxWidth: "33.33%" }}>
            <Tooltip title={formatString(AppStrings.PROFIT_BOOST_REALIZED_VALUE_TOOLTIP, { currency: AppStrings.PRIMARY_CURRENCY })}>
              <InfoCircleOutlined
                style={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  color: "var(--grey-400)",
                  fontSize: 14,
                  cursor: "help",
                  zIndex: 1,
                }}
              />
            </Tooltip>
            <KpiCard
              title={AppStrings.PROFIT_BOOST_REALIZED_VALUE}
              value={Number(k.profit_boosts_amount_realized ?? 0)}
              percentage={k.profit_boosts_amount_realized_pct_change as number}
              hugeValue={false}
              backgroundColor={KPI_CARD_BACKGROUND.DARK}
              isApiRunning={isKpiLoading}
              maximumFractionDigits={2}
            />
          </div>
          <div style={{ position: "relative", flex: 1, maxWidth: "33.33%" }}>
            <KpiCard
              title={`${AppStrings.DELTA_SYMBOL} ${AppStrings.PROFIT_BOOSTS}`}
              value={Number(k.profit_boosts_given ?? 0)}
              percentage={k.profit_boosts_given_pct_change as number}
              hugeValue={false}
              backgroundColor={KPI_CARD_BACKGROUND.DARK}
              isApiRunning={isKpiLoading}
              maximumFractionDigits={0}
            />
          </div>
        </div>
        <div className={styles["row"]} style={{ display: showAdditionalKpis && showCasinoKpi ? "flex" : "none" }}>
          <div style={{ position: "relative", flex: 1, maxWidth: "33.33%" }}>
            <Tooltip title={formatString(AppStrings.FREE_SPINS_REALIZED_VALUE_TOOLTIP, { currency: AppStrings.PRIMARY_CURRENCY })}>
              <InfoCircleOutlined
                style={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  color: "var(--grey-400)",
                  fontSize: 14,
                  cursor: "help",
                  zIndex: 1,
                }}
              />
            </Tooltip>
            <KpiCard
              title={AppStrings.FREE_SPINS_REALIZED_VALUE}
              value={Number(k.free_spins_amount_realized ?? 0)}
              percentage={k.free_spins_amount_realized_pct_change as number}
              hugeValue={false}
              backgroundColor={KPI_CARD_BACKGROUND.DARK}
              isApiRunning={isKpiLoading}
              maximumFractionDigits={2}
            />
          </div>
          <div style={{ position: "relative", flex: 1, maxWidth: "33.33%" }}>
            <KpiCard
              title={`${AppStrings.DELTA_SYMBOL} ${AppStrings.FREE_SPINS}`}
              value={Number(k.free_spins_given ?? 0)}
              percentage={k.free_spins_given_pct_change as number}
              hugeValue={false}
              backgroundColor={KPI_CARD_BACKGROUND.DARK}
              isApiRunning={isKpiLoading}
              maximumFractionDigits={0}
            />
          </div>
        </div>
      </div>
      <div className={styles["promos-manual-actions-table-container"]}>
        {agentPerformanceView ? (
          <AgentPerformanceView
            columns={agentPerformanceColumns}
            handleTableChange={handleTableChange}
            filteredData={apvDataSource}
            roleOptions={roleOptions}
            selectedRoles={selectedRole}
            onRoleChange={setSelectedRole}
            scrollX={2800}
            pagination={{
              current: currentPage,
              pageSize,
              total: totalResults,
              onChange: handlePaginationChange,
            }}
            isLoading={tableLoading}
          />
        ) : (
          <TypedDataTable
            columns={columns}
            dataSource={tableItems}
            loading={tableLoading}
            pagination={{
              current: currentPage,
              pageSize,
              total: totalResults,
              onChange: handlePaginationChange,
            }}
            onChange={handleTableChange}
            rowKey={(record: any) => record.event_id}
            scroll={{ x: 1500, y: 500 }}
          />
        )}
      </div>
    </div>
  );
}

export default PromosManualActions;

